// ── Core identifiers ──────────────────────────────────────────────────────────

export type FieldId =
  | 'arte'
  | 'cultura'
  | 'movimiento'
  | 'sobrenatural'
  | 'supervivencia'
  | 'vigor'

export type RaceId =
  | 'humano'
  | 'kordun'
  | 'namester'
  | 'nergal'
  | 'zannin'
  | 'urcan'
  | 'atmar'
  | 'ernamin'
  | 'voggar'
  | 'wuton'

export type VitalStageId = 'juventud' | 'madurez' | 'vejez'

export type MagicPath =
  | 'arcanomancia'
  | 'kairomancia'
  | 'fisiomancia'
  | 'animancia'
  | 'teomancia'

export type Sex = 'masculino' | 'femenino' | 'otro'

// ── Data definitions ─────────────────────────────────────────────────────────

export interface Race {
  id: RaceId
  name: string
  description: string
  lore: string
  image: string                    // ruta a imagen por defecto (variante masculina)
  shield?: string                  // ruta al escudo/emblema de la raza
  speciesImages?: { male: string; female: string }  // imágenes por sexo
  perkImages?: string[]            // imágenes de habilidades/perks específicos de raza
  forcedSex?: Sex                  // si la raza fuerza un género concreto
  fieldBonuses: Partial<Record<FieldId, number>>
  fieldMaxOverrides: Partial<Record<FieldId, number>>
  skillBonuses: Record<string, number>
  specialtyBonuses: Record<string, number>
  miscBonuses: string[]
  traits: string[]
  dimorphism?: {
    male: {
      fieldBonuses?: Partial<Record<FieldId, number>>
      skillBonuses?: Record<string, number>
    }
    female: {
      fieldBonuses?: Partial<Record<FieldId, number>>
      skillBonuses?: Record<string, number>
    }
  }
}

export interface VitalStageData {
  id: VitalStageId
  name: string
  description: string
  fieldBonuses: Partial<Record<FieldId, number>>
  extraFieldPoints: number
}

export interface FieldDef {
  id: FieldId
  name: string
  color: string
  description: string
}

export interface SkillDef {
  id: string
  name: string
  field: FieldId
}

export interface SpecialtyDef {
  id: string
  name: string
  skill: string
}

export type Requirement = string | { name: string; value: number }

// ── Efectos matemáticos de méritos y defectos ─────────────────────────────────

/** Tipo de stat al que apunta el efecto */
export type EffectTargetType = 'field' | 'skill' | 'specialty' | 'blindaje' | 'regeneracion'

/** Operación del efecto (actualmente solo 'add'; value negativo = penalizador) */
export type EffectOp = 'add'

/**
 * Efecto matemático que un mérito o defecto aplica automáticamente a la ficha.
 * targetType + targetId identifican la stat; op y value describen el cambio.
 */
export interface CharacterEffect {
  targetType: EffectTargetType
  targetId:   string   // id de campo, habilidad o especialidad
  op:         EffectOp
  value:      number   // positivo = bono, negativo = penalizador
}

export interface Merit {
  id: string
  title: string
  cost: number
  requirements: Requirement[]
  description: string
  group?: string
  /** Efectos matemáticos que aplica este mérito (por defecto ninguno) */
  effects?: CharacterEffect[]
  /** Si true, la ficha ignora los efectos matemáticos (default: true en entradas nuevas) */
  ignoreEffects?: boolean
}

export interface Defect {
  id: string
  title: string
  gain: number
  requirements: Requirement[]
  description: string
  group?: string
  effects?: CharacterEffect[]
  ignoreEffects?: boolean
}

export interface Competence {
  id: string
  title: string
  cost: number
  requirements: Requirement[]
  description: string
  group?: string
  multiple?: boolean
}

export interface MartialAbility {
  id: string
  title: string
  cost: number
  requirements: Requirement[]
  description: string
  group?: string
}

export interface Mutation {
  id: string
  title: string
  description: string
  requirements: string[]  // mutation IDs or "field:fieldId:minValue"
  multiple?: boolean      // can be taken multiple times
  onlyOnce?: boolean      // can only be taken once
}

export interface Implant {
  id: string
  title: string
  description: string
}

export interface MagicSpell {
  id: string
  title: string
  cost: number
  path: MagicPath
  requirements: string[]
  description: string
  lvl?: number
  manaCost?: number
  threshold?: number
  // null = hechizo sin desafío (así vienen los datos migrados); undefined = no aplica
  challenge?: number | null
}

// ── Inventory ─────────────────────────────────────────────────────────────────

export type ItemCategory = 'arma' | 'armadura' | 'herramienta' | 'consumible' | 'misc'

export interface InventoryItem {
  id: string
  name: string
  category: ItemCategory
  quantity: number
  weight?: number       // kg
  description?: string
  equipped?: boolean
}

// ── Equipment (artefactos del catálogo global equipados en el personaje) ──────

export type EquippedItemType = 'weapon' | 'armor' | 'object'

/**
 * Referencia a un artefacto global equipado.
 * Almacena los efectos como snapshot al momento de equipar,
 * de modo que los bonos/penalizadores sean independientes de ediciones posteriores al ítem.
 */
export interface EquippedItem {
  itemId:    string
  itemType:  EquippedItemType
  name:      string            // nombre de display (snapshot)
  effects:   CharacterEffect[] // snapshot de efectos al equipar
  equipReq?: string            // JSON { spec, val } — req de equipamiento snapshotado para cálculo de sobrecarga
}

// ── Language ──────────────────────────────────────────────────────────────────

export type LanguageProficiency = 'Nativo' | 'Fluido' | 'Básico'

export interface LanguageEntry {
  name: string
  proficiency: LanguageProficiency
}

// ── Character sheet ───────────────────────────────────────────────────────────

export interface Character {
  id: string
  name: string
  concept: string
  race: RaceId
  sex: Sex
  vitalStage: VitalStageId
  fieldPoints: Record<FieldId, number>
  skillPoints: Record<string, number>
  specialtyPoints: Record<string, number>
  customSpecialties: Record<string, string[]>
  merits: string[]
  defects: string[]
  competences: string[]
  martial: Record<string, number>
  magic: Record<string, number>
  selectedPaths: MagicPath[]
  mutations?: string[]  // IDs of selected mutations (Zanin only)
  implants?: string[]   // IDs de implantes seleccionados (solo Námester)
  xp: number          // Puntos de experiencia disponibles para avance
  // Valores rastreados durante el juego (opcionales)
  regeneracion?: number    // Modificador manual a la regeneración diaria (base = vigor total)
  hpNaturalCurrent?: number
  hpNaturalTotal?: number
  hpSobrenaturalCurrent?: number
  hpSobrenaturalTotal?: number
  hpAnimicaCurrent?: number
  hpAnimicaTotal?: number
  blindaje?: number         // base manual (equipo + bonos de méritos/defectos)
  blindajeCurrent?: number  // puntos actuales de blindaje (se degrada en combate)
  fatigueCurrent?: number
  fatigueTotal?: number
  manaCurrent?: number
  manaTotal?: number
  marcialCurrent?: number
  marcialTotal?: number
  // Condiciones temporales en combate/juego
  conditions?: string[]
  // Inventario libre del personaje
  inventory?: InventoryItem[]
  // Artefactos del catálogo global actualmente equipados
  equippedItems?: EquippedItem[]
  // Trasfondo del personaje (opcionales)
  quote?: string
  history?: string
  objetivo?: string
  languages?: LanguageEntry[]
  equipment?: string
  createdAt: string
  updatedAt: string
}

// ── Creation wizard state ─────────────────────────────────────────────────────

export interface WizardStep {
  id: string
  label: string
  icon: string
}

export const WIZARD_STEPS: WizardStep[] = [
  { id: 'race',        label: 'Raza',          icon: '⚔' },
  { id: 'vital',       label: 'Estadío',        icon: '⧗' },
  { id: 'fields',      label: 'Campos',         icon: '◈' },
  { id: 'skills',      label: 'Habilidades',    icon: '◇' },
  { id: 'specialties', label: 'Especialidades', icon: '✦' },
  { id: 'merits',      label: 'Méritos',        icon: '✧' },
  { id: 'competences', label: 'Competencias',   icon: '⊞' },
  { id: 'martial',     label: 'Marciales',      icon: '⊕' },
  { id: 'magic',       label: 'Magias',         icon: '✶' },
  { id: 'mutations',   label: 'Mutaciones',     icon: '⊗' },
  { id: 'implants',    label: 'Implantes',      icon: '⚙' },
  { id: 'backstory',   label: 'Trasfondo',      icon: '✎' },
  { id: 'summary',     label: 'Resumen',        icon: '✓' },
]

// ── Dice table ────────────────────────────────────────────────────────────────

export const DICE_TABLE: Record<number, string> = {
  0: '1d8',
  1: '1d10',
  2: '1d12',
  3: '1d20',
  4: '1d20',
  5: '2d20',
}

// ── Creation budget constants ─────────────────────────────────────────────────

export const BASE_FIELD_POINTS = 5    // puntos de campo base a repartir
export const BASE_SKILL_POINTS = 30   // puntos de habilidad base
export const BASE_SPEC_POINTS  = 60  // puntos de especialidad base
export const BASE_XP           = 100  // XP base para méritos/defectos
export const MAX_FIELD_LEVEL   = 3    // máximo por campo sin modificadores de raza
